/**
 * Annotation tools and their device-local preferences (last tool + per-tool settings, recently used
 * symbols, "Apple Pencil seen", coach marks). Pure helpers + guarded localStorage access.
 */
import {
  CONNECTOR_SIZE_RANGE,
  DEFAULT_CONNECTOR_SIZE,
  HIGHLIGHT_COLORS,
  HIGHLIGHT_WIDTHS,
  INK_COLORS,
  PEN_COLORS,
  PEN_WIDTHS,
  STAMP_SIZE_RANGE,
  TEXT_SIZES,
} from '../../shared/annotations';
import { isSmuflGlyphName } from '../../shared/smufl';

export const TOOL_IDS = ['pen', 'highlighter', 'text', 'stamp', 'connector', 'eraser', 'select'] as const;
export type ToolId = (typeof TOOL_IDS)[number];

/** Tools whose main gesture is a drag (stroke / segment / erase). */
export const DRAG_TOOLS: readonly ToolId[] = ['pen', 'highlighter', 'eraser'];
/** Tools that act on a single tap. */
export const TAP_TOOLS: readonly ToolId[] = ['text', 'stamp', 'connector'];

/** Text size slider range (1/Q of page width); the schema allows 80..1200. */
export const TEXT_SIZE_RANGE = [100, 700] as const;
export const DEFAULT_STAMP_SIZE = 420;

export interface ToolPrefs {
  tool: ToolId;
  /** width = index into PEN_WIDTHS */
  pen: { color: string; width: number };
  /** width = index into HIGHLIGHT_WIDTHS */
  highlighter: { color: string; width: number };
  text: { color: string; size: number };
  stamp: { color: string; size: number; glyph: string | null };
  /** size = pin diameter in 1/Q of page width (CONNECTOR_SIZE_RANGE) */
  connector: { color: string; size: number };
}

export const DEFAULT_TOOL_PREFS: ToolPrefs = {
  tool: 'pen',
  pen: { color: PEN_COLORS[0], width: 1 },
  highlighter: { color: HIGHLIGHT_COLORS[0], width: 1 },
  text: { color: PEN_COLORS[0], size: TEXT_SIZES[1] },
  stamp: { color: PEN_COLORS[0], size: DEFAULT_STAMP_SIZE, glyph: null },
  connector: { color: PEN_COLORS[1], size: DEFAULT_CONNECTOR_SIZE },
};

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const pick = <T extends string>(v: unknown, allowed: readonly T[], fallback: T): T =>
  typeof v === 'string' && (allowed as readonly string[]).includes(v.toLowerCase()) ? (v.toLowerCase() as T) : fallback;
const intIn = (v: unknown, min: number, max: number, fallback: number): number =>
  typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max ? v : fallback;

/** Validates stored preferences field by field; anything unknown falls back to the default. */
export function parseToolPrefs(raw: unknown): ToolPrefs {
  const d = DEFAULT_TOOL_PREFS;
  if (!isObj(raw)) return d;
  const sub = (k: string): Record<string, unknown> => (isObj(raw[k]) ? (raw[k] as Record<string, unknown>) : {});
  const pen = sub('pen');
  const hl = sub('highlighter');
  const text = sub('text');
  const stamp = sub('stamp');
  const conn = sub('connector');
  const penColors = PEN_COLORS as readonly string[];
  return {
    tool: pick(raw.tool, TOOL_IDS, d.tool),
    pen: { color: pick(pen.color, INK_COLORS as readonly string[], d.pen.color), width: intIn(pen.width, 0, PEN_WIDTHS.length - 1, d.pen.width) },
    highlighter: {
      color: pick(hl.color, HIGHLIGHT_COLORS as readonly string[], d.highlighter.color),
      width: intIn(hl.width, 0, HIGHLIGHT_WIDTHS.length - 1, d.highlighter.width),
    },
    text: { color: pick(text.color, penColors, d.text.color), size: intIn(text.size, TEXT_SIZE_RANGE[0], TEXT_SIZE_RANGE[1], d.text.size) },
    stamp: {
      color: pick(stamp.color, penColors, d.stamp.color),
      size: intIn(stamp.size, STAMP_SIZE_RANGE[0], STAMP_SIZE_RANGE[1], d.stamp.size),
      glyph: isSmuflGlyphName(stamp.glyph) ? stamp.glyph : null,
    },
    connector: {
      color: pick(conn.color, penColors, d.connector.color),
      size: intIn(conn.size, CONNECTOR_SIZE_RANGE[0], CONNECTOR_SIZE_RANGE[1], d.connector.size),
    },
  };
}

/** Most recently used first, without duplicates, capped. */
export function pushRecent(list: readonly string[], name: string, max = 10): string[] {
  return [name, ...list.filter((n) => n !== name)].slice(0, max);
}

export function parseRecentGlyphs(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const v of raw) if (isSmuflGlyphName(v) && !out.includes(v)) out.push(v);
  return out.slice(0, 10);
}

/* ─── localStorage (never throws) ───────────────────────────────────────── */

export const STORAGE = {
  tools: 'kgr.annotate.tools',
  recentGlyphs: 'kgr.annotate.recentGlyphs',
  coach: 'kgr.annotate.coach',
  penSeen: 'kgr.penSeen',
} as const;

function readJson(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? null : (JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable – lasts for this session only */
  }
}

export const loadToolPrefs = (): ToolPrefs => parseToolPrefs(readJson(STORAGE.tools));
export const saveToolPrefs = (p: ToolPrefs): void => writeJson(STORAGE.tools, p);
export const loadRecentGlyphs = (): string[] => parseRecentGlyphs(readJson(STORAGE.recentGlyphs));
export const saveRecentGlyphs = (list: readonly string[]): void => writeJson(STORAGE.recentGlyphs, list);
export const readPenSeen = (): boolean => readJson(STORAGE.penSeen) === true || readJson(STORAGE.penSeen) === 1;
export const writePenSeen = (): void => writeJson(STORAGE.penSeen, true);
export const readCoachDone = (): boolean => readJson(STORAGE.coach) === true;
export const writeCoachDone = (): void => writeJson(STORAGE.coach, true);
